import { Pencil, Plus, MessageSquare, Search } from "lucide-react"
import { useEffect, useState } from "preact/hooks"
import { Button } from "../../../../components/ui/button"
import { Input } from "../../../../components/ui/input"
import { Textarea } from "../../../../components/ui/textarea"
import type { CalendarPromotionLoadState } from "../../promo_creation/hooks/use_promotional_context_api"
import { CalendarInfo, Promotion } from "../../promo_creation/types"
import { PromotionCard } from './promotion_card'
import { AddPromotionForm } from '../../promo_creation/components/promotion_forms'
import { CalendarFormData, EditCalendarForm } from '../../promo_creation/components/calendar_forms'
import { EnhancedPromotionEditModal } from './enhanced_promotion_edit_modal'

interface CalendarListProps {
  calendars: CalendarInfo[]
  is_loading_calendars: boolean
  calendars_load_error: string | null
  promotion_load_by_calendar: Record<string, CalendarPromotionLoadState>
  searchTerm: string
  onSearchChange: (term: string) => void
  openCalendarId: string | null
  setOpenCalendarId: (id: string | null) => void
  onUpdatePromotion: (promotion: Promotion) => void
  onDeletePromotion: (promotionId: string) => void
  onCreatePromotion: (calendarId: string, promotion: Promotion) => void
  onUpdateCalendar: (calendarId: string, calendarData: CalendarFormData) => Promise<boolean>
  isUpdatingCalendar: boolean
  feedback: string
  onFeedbackChange: (feedback: string) => void
  onProvideFeedback: (calendar: CalendarInfo) => void
  isProvidingFeedback: boolean
  isAddingPromotion: boolean
  addingPromotionToCalendar: string | null
  setAddingPromotionToCalendar: (id: string | null) => void
}

export function CalendarList({ 
  calendars, 
  is_loading_calendars,
  calendars_load_error: _calendars_load_error,
  promotion_load_by_calendar,
  searchTerm,
  onSearchChange,
  openCalendarId, 
  setOpenCalendarId, 
  onUpdatePromotion,
  onDeletePromotion,
  onCreatePromotion,
  onUpdateCalendar,
  isUpdatingCalendar,
  feedback,
  onFeedbackChange,
  onProvideFeedback,
  isProvidingFeedback,
  isAddingPromotion,
  addingPromotionToCalendar,
  setAddingPromotionToCalendar
}: CalendarListProps) {
  const [editingCalendarId, setEditingCalendarId] = useState<string | null>(null)

  if (is_loading_calendars && calendars.length === 0) {
    return <CalendarListSkeleton />
  }
  if (calendars.length === 0) return null

  async function handleSaveCalendar(calendarId: string, calendarData: CalendarFormData) {
    const success = await onUpdateCalendar(calendarId, calendarData)
    if (success) {
      setEditingCalendarId(null)
    }
  }

  const filteredCalendars = getFilteredCalendars(calendars, searchTerm)
  const hasActiveSearch = searchTerm.trim().length > 0

  useEffect(() => {
    if (!hasActiveSearch) return

    if (filteredCalendars.length === 0) {
      if (openCalendarId !== null) {
        setOpenCalendarId(null)
      }
      return
    }

    const hasOpenMatchedCalendar = filteredCalendars.some((calendar) => calendar.id === openCalendarId)
    if (!hasOpenMatchedCalendar) {
      setOpenCalendarId(filteredCalendars[0].id)
    }
  }, [hasActiveSearch, filteredCalendars, openCalendarId, setOpenCalendarId])

  return (
    <div id="calendar-list-section" class="bg-gray-50 p-4 rounded-lg">
      <div class="flex items-center justify-between gap-4 mb-3">
        <h4 class="font-medium text-gray-900">Current Promotional Calendars</h4>
        <div class="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchTerm}
            onInput={(e) => onSearchChange(e.currentTarget.value)}
            placeholder="Search promotions, IDs, products..."
            className="pl-10"
          />
        </div>
      </div>
      <div class="space-y-2">
        {filteredCalendars.length === 0 && (
          <div class="border border-gray-200 rounded-lg bg-white px-4 py-6 text-sm text-gray-500">
            No calendars or promotions match your search.
          </div>
        )}
        {filteredCalendars.map((calendar) => {
          const isOpen = openCalendarId === calendar.id
          return (
            <div key={calendar.id} class="border border-gray-200 rounded-lg bg-white">
              <CalendarHeader
                calendar={calendar}
                isOpen={isOpen}
                onToggle={() => setOpenCalendarId(isOpen ? null : calendar.id)}
                onEdit={() => {
                  setEditingCalendarId(calendar.id)
                  setOpenCalendarId(calendar.id)
                }}
              />

              {editingCalendarId === calendar.id && (
                <div class="px-4 pb-4">
                  <EditCalendarForm
                    calendar={calendar}
                    onSave={(calendarData) => handleSaveCalendar(calendar.id, calendarData)}
                    onCancel={() => setEditingCalendarId(null)}
                    isSaving={isUpdatingCalendar}
                  />
                </div>
              )}

              {isOpen && (
                <CalendarContent
                  calendar={calendar}
                  onUpdatePromotion={onUpdatePromotion}
                  onDeletePromotion={onDeletePromotion}
                  onCreatePromotion={onCreatePromotion}
                  promotion_load_row={promotion_load_by_calendar[calendar.id]}
                  feedback={feedback}
                  onFeedbackChange={onFeedbackChange}
                  onProvideFeedback={onProvideFeedback}
                  isProvidingFeedback={isProvidingFeedback}
                  isAddingPromotion={isAddingPromotion}
                  addingPromotionToCalendar={addingPromotionToCalendar}
                  setAddingPromotionToCalendar={setAddingPromotionToCalendar}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CalendarListSkeleton() {
  return (
    <div id="calendar-list-section" class="bg-gray-50 p-4 rounded-lg animate-pulse">
      <div class="h-6 w-64 bg-gray-200 rounded mb-3" />
      <div class="space-y-2">
        {[1, 2, 3].map((row_key) => (
          <div key={row_key} class="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div class="h-12 bg-gray-100" />
            <div class="p-3 space-y-2 border-t border-gray-100">
              <div class="h-3 w-1/3 bg-gray-100 rounded" />
              <div class="h-3 w-full bg-gray-50 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface PromotionCardSkeletonProps {
  slot_index: number
}

function PromotionCardSkeleton({ slot_index }: PromotionCardSkeletonProps) {
  return (
    <div
      class="rounded-lg border border-dashed border-gray-200 bg-white p-3 space-y-2 animate-pulse"
      aria-hidden={true}
    >
      <div class="flex justify-between">
        <div class="space-y-1">
          <div class="h-3 w-32 bg-gray-200 rounded" />
          <div class="h-2 w-44 bg-gray-100 rounded" />
        </div>
        <div class="h-5 w-10 bg-gray-100 rounded-full" />
      </div>
      <div class="h-8 w-full bg-gray-50 rounded" />
      <span class="sr-only">Loading promotion placeholder {slot_index}</span>
    </div>
  )
}

function getFilteredCalendars(calendars: CalendarInfo[], searchTerm: string): CalendarInfo[] {
  const normalizedSearch = searchTerm.trim().toLowerCase()
  if (!normalizedSearch) return calendars

  return calendars
    .map((calendar) => {
      const matchesCalendar = matchesCalendarSearch(calendar, normalizedSearch)
      const promotions = matchesCalendar
        ? calendar.promotions
        : calendar.promotions.filter((promotion) => matchesPromotionSearch(promotion, normalizedSearch))

      return promotions.length > 0 || matchesCalendar
        ? { ...calendar, promotions }
        : null
    })
    .filter((calendar): calendar is CalendarInfo => calendar !== null)
}

function matchesCalendarSearch(calendar: CalendarInfo, searchTerm: string): boolean {
  return (
    calendar.calendar_name.toLowerCase().includes(searchTerm) ||
    calendar.calendar_description?.toLowerCase().includes(searchTerm) ||
    calendar.retailer_name?.toLowerCase().includes(searchTerm) ||
    calendar.brand_name?.toLowerCase().includes(searchTerm)
  ) ?? false
}

function matchesPromotionSearch(promotion: Promotion, searchTerm: string): boolean {
  const matchesName = promotion.name.toLowerCase().includes(searchTerm)
  const matchesDescription = promotion.description?.toLowerCase().includes(searchTerm) || false
  const matchesProducts = promotion.products.some((product) => product.toLowerCase().includes(searchTerm))
  const matchesRemoteId = promotion.remote_promo_id?.toLowerCase().includes(searchTerm) || false
  return matchesName || matchesDescription || matchesProducts || matchesRemoteId
}

interface CalendarHeaderProps {
  calendar: CalendarInfo
  isOpen: boolean
  onToggle: () => void
  onEdit: () => void
}

function CalendarHeader({ calendar, isOpen, onToggle, onEdit }: CalendarHeaderProps) {
  // A div with a nested icon button (not a button inside a button, which is invalid HTML).
  return (
    <div
      class="w-full flex justify-between items-center px-4 py-3 text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200"
      onClick={onToggle}
    >
      <div>
        <div class="font-semibold text-gray-900">{calendar.calendar_name}</div>
        <div class="text-xs text-gray-500">
          {calendar.calendar_description || "No description"}
          {calendar.calendar_year && (
            <span> &middot; {calendar.calendar_year}</span>
          )}
          {calendar.retailer_name && (
            <span> &middot; {calendar.retailer_name}</span>
          )}
          {calendar.brand_name && (
            <span> &middot; {calendar.brand_name}</span>
          )}
        </div>
      </div>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="Edit calendar details"
          onClick={(event) => {
            event.stopPropagation()
            onEdit()
          }}
        >
          <Pencil className="h-4 w-4" />
        </button>
        <span class="ml-1 text-gray-400">{isOpen ? "▲" : "▼"}</span>
      </div>
    </div>
  )
}

interface CalendarContentProps {
  calendar: CalendarInfo
  onUpdatePromotion: (promotion: Promotion) => void
  onDeletePromotion: (promotionId: string) => void
  onCreatePromotion: (calendarId: string, promotion: Promotion) => void
  promotion_load_row?: CalendarPromotionLoadState
  feedback: string
  onFeedbackChange: (feedback: string) => void
  onProvideFeedback: (calendar: CalendarInfo) => void
  isProvidingFeedback: boolean
  isAddingPromotion: boolean
  addingPromotionToCalendar: string | null
  setAddingPromotionToCalendar: (id: string | null) => void
}

function CalendarContent({
  calendar,
  onUpdatePromotion,
  onDeletePromotion,
  onCreatePromotion,
  promotion_load_row,
  feedback,
  onFeedbackChange,
  onProvideFeedback,
  isProvidingFeedback,
  isAddingPromotion,
  addingPromotionToCalendar,
  setAddingPromotionToCalendar,
}: CalendarContentProps) {
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  function handleEditPromotion(promotion: Promotion) {
    setEditingPromotion(promotion)
    setIsEditModalOpen(true)
  }

  function handleCloseEditModal() {
    setEditingPromotion(null)
    setIsEditModalOpen(false)
  }

  function handleSavePromotion(updatedPromotion: Promotion) {
    onUpdatePromotion(updatedPromotion)
    handleCloseEditModal()
  }

  return (
    <div class="p-4 border-t">
      <div class="grid gap-4">
        {calendar.promotions.length === 0 && !promotion_load_row?.loading_initial ? (
          <div class="text-sm text-gray-500">No promotions found for this calendar.</div>
        ) : null}
        {calendar.promotions.length > 0
          ? calendar.promotions.map((promotion, index) => (
              <PromotionCard
                key={index}
                promotion={promotion}
                onEdit={handleEditPromotion}
                onDelete={promotion.id ? () => onDeletePromotion(promotion.id!) : undefined}
              />
            ))
          : null}
        {promotion_load_row?.loading_initial && calendar.promotions.length === 0 ? (
          <div class="grid gap-2">
            {[0, 1, 2].map((slot_index) => (
              <PromotionCardSkeleton key={slot_index} slot_index={slot_index} />
            ))}
          </div>
        ) : null}
        {promotion_load_row?.loading_more ? (
          <div class="text-xs text-gray-400">Loading more promotions…</div>
        ) : null}
      </div>
      
      {/* Add Promotion Button */}
      <div class="mt-4">
        <Button
          onClick={() => setAddingPromotionToCalendar(calendar.id)}
          disabled={isAddingPromotion}
          class="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Promotion
        </Button>
      </div>

      {/* Add Promotion Form */}
      {addingPromotionToCalendar === calendar.id && (
        <AddPromotionForm
          calendar={calendar}
          onSave={(promotion) => onCreatePromotion(calendar.id, promotion)}
          onCancel={() => setAddingPromotionToCalendar(null)}
          isSaving={isAddingPromotion}
        />
      )}

      {/* Feedback Section - Only show for AI-generated calendars */}
      {calendar.source_type === 'ai_upload' && (
        <div class="border-t pt-4 mt-4">
          <div class="flex items-center gap-2 mb-2">
            <MessageSquare className="h-4 w-4 text-gray-400" />
            <h5 class="text-sm font-medium text-gray-700">Provide Feedback for Re-analysis:</h5>
          </div>
          <div class="flex gap-2">
            <Textarea
              value={feedback}
              onChange={(e) => onFeedbackChange(e.currentTarget.value)}
              placeholder="Provide feedback to improve the analysis..."
              class="flex-1"
              rows={3}
            />
            <Button
              onClick={() => onProvideFeedback(calendar)}
              disabled={!feedback.trim() || isProvidingFeedback}>
              {isProvidingFeedback ? "Applying..." : "Apply Feedback"}
            </Button>
          </div>
        </div>
      )}
      
      <div class="text-xs text-gray-400 mt-4">
        Uploaded: {new Date(calendar.created_at).toLocaleString()}
      </div>

      <EnhancedPromotionEditModal
        promotion={editingPromotion}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSavePromotion}
        onDelete={onDeletePromotion}
      />
    </div>
  )
}
